import {
  Box,
  Heading,
  lgBumps,
  mdBumps,
  Notification,
  SecondaryButton,
  Typo,
  useModal,
  xlBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'
import { generatePath, Navigate, useNavigate, useSearchParams } from 'react-router'

import { with404ErrorHandling } from '../../clients/api'
import { Data, Proposal } from '../../domain'
import { useErrorHandlingAsyncCallback } from '../../hooks/errors'
import {
  TExtends,
  TKey,
  useWriskTranslation,
} from '../../infrastructure/internationalisation'
import { PrefillConfig, Scheme } from '../../state/configuration'
import { AppPath } from '../AppPath'
import { usePolicyholder, usePrincipal } from '../authentication'
import { FormErrors } from '../formBuilder'
import { FullPage } from '../templates'
import { PrefillModalFailed } from './PrefillModalFailed'
import { PrefillModalSuccess } from './PrefillModalSuccess'

const tKey = TKey('pages.prefill')

export interface PrefillPageProps {
  scheme: Scheme
  prefill: PrefillConfig
}

export const PrefillPage: FunctionComponent<PrefillPageProps> = ({
  scheme,
  prefill: { form },
}) => {
  const { apiClient } = usePrincipal()
  const { refresh } = usePolicyholder()
  const { show, hide } = useModal()

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { t } = useWriskTranslation()

  const handleSkip = useCallback(() => {
    hide()
    navigate(
      [generatePath(AppPath.PRODUCT, scheme), searchParams.toString()]
        .filter(Boolean)
        .join('?'),
    )
  }, [scheme])

  const handleContinue = useCallback(
    (proposal: Proposal) => {
      hide()
      refresh()

      navigate(
        [
          generatePath(AppPath.PROPOSAL, {
            productUrlName: scheme.productUrlName,
            proposalCode: proposal.proposalCode,
          }),
          searchParams.toString(),
        ]
          .filter(Boolean)
          .join('?'),
      )
    },
    [scheme, searchParams],
  )

  const handleSubmit = useErrorHandlingAsyncCallback(async ({ email, ...data }: Data) => {
    if (!email) {
      throw new Error('An email must be configured as one of the questions for prefill')
    }

    const proposalPrefill = await with404ErrorHandling(() =>
      apiClient.retrievePrefill({
        schemeCode: scheme.schemeCode,
        email,
        data,
      }),
    )

    show({
      content: proposalPrefill ? (
        <PrefillModalSuccess
          proposalPrefill={proposalPrefill}
          onContinue={handleContinue}
          tKey={tKey}
        />
      ) : (
        <PrefillModalFailed scheme={scheme} onSkip={handleSkip} tKey={tKey} />
      ),
    })
  })

  return form ? (
    <FullPage
      pageId='prefill-page'
      header={t(tKey('header'))}
      subheader={t(tKey('subheader'))}
    >
      <Box display='flex' width={1} rowGap={xlBumps} flexDirection='column'>
        <Box width={1} variant='raised' p={lgBumps}>
          <FormErrors
            variant='short'
            id='prefill'
            data={{}}
            config={form}
            onSubmit={handleSubmit.execute}
            loading={handleSubmit.loading}
            tKey={TExtends(tKey, 'form')}
            footerText='footer'
            t={t}
          />
        </Box>
        <Notification
          type='standalone'
          rowGap={mdBumps}
          p={lgBumps}
          flexDirection='column'
          alignItems='flex-start'
          justifyContent='flex-start'
        >
          <Heading typoSize='md'>{t(tKey('skip', 'header'))}</Heading>
          <Typo>{t(tKey('skip', 'content'))}</Typo>
          <SecondaryButton onClick={handleSkip}>
            {t(tKey('skip', 'actions', 'continue'))}
          </SecondaryButton>
        </Notification>
      </Box>
    </FullPage>
  ) : (
    <Navigate to='..' relative='path' />
  )
}
